import { useCallback } from "react";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNotifications } from "@/context/NotificationsContext";
import { healthOf, healthRowClass } from "@/lib/health";
import { cn } from "@/lib/utils";

// Red dot for a line item / tile with activity you haven't opened yet.
export const NewDot = ({ show, className = "", title = "New activity" }) =>
  show ? <span className={cn("cc-new-dot", className)} title={title} data-testid="new-dot" /> : null;

// One quiet line that says why a row is marked (icon + reason), coloured by level.
// (A red DOT always means "new activity" — never reuse it for severity.)
export function HealthChip({ h, className = "" }) {
  if (!h) return null;
  const critical = h.level === "critical";
  return (
    <span title={h.reason} data-testid={`health-${h.level}`}
      className={cn("inline-flex items-center gap-1.5 text-[11px] leading-tight max-w-[260px]", critical ? "text-red-700" : "text-amber-700", className)}>
      {critical ? <Icons.AlertTriangle className="h-3 w-3 shrink-0" /> : <Icons.Hourglass className="h-3 w-3 shrink-0" />}
      <span className="truncate">{h.reason}</span>
    </span>
  );
}

// Everything a list needs for one collection: new-dot, health, row class, mark-opened.
// section = the sidebar section this page belongs to (rows are compared against it).
export function useRowSignals(coll, section) {
  const { isRowNew, seeItems } = useNotifications();
  const isNew = useCallback((id) => isRowNew(coll, id, section), [isRowNew, coll, section]);
  const opened = useCallback((id) => seeItems([`${coll}/${id}`]), [seeItems, coll]);
  const health = useCallback((doc) => healthOf(coll, doc), [coll]);
  const rowClass = useCallback((doc) => healthRowClass(healthOf(coll, doc)), [coll]);
  return { isNew, opened, health, rowClass };
}

// Page-level "N new · Mark all seen" control.
export function NewActivityBar({ section, colls, label = "new updates" }) {
  const { newRowsIn, seeRows } = useNotifications();
  const n = newRowsIn(section, colls);
  if (!n) return null;
  return (
    <div className="flex items-center gap-2 rounded-full border border-red-200 bg-red-50/70 pl-3 pr-1 py-1 text-xs text-red-700" data-testid={`new-activity-${section}`}>
      <span className="cc-new-dot" /><span className="font-medium">{n} {label}</span>
      <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-red-700 hover:bg-red-100" onClick={() => seeRows([section])} data-testid={`mark-seen-${section}`}>Mark all seen</Button>
    </div>
  );
}

// Tab label with a red dot when anything inside has unseen activity.
export const TabDot = ({ show }) => (show ? <span className="cc-new-dot ml-1.5 h-1.5 w-1.5" /> : null);
